import { tuning } from '@/config/tuning';
import {
  clampStickOrigin,
  fitTouchProfile,
  followStickOrigin,
  shapeAxis,
  smoothTowards,
  type TouchProfile,
} from '@/input/touchTuning';
import { subscribeViewport, type ViewportUnsubscribe } from '@/ui/viewport';

/**
 * VirtualJoystick.ts — a floating on-screen joystick for touch (and mouse).
 *
 * It's visible at rest in a lower thumb corner (left by default; right when the
 * left-handed layout is on — M31). Touch anywhere on the joystick's ~65% of the
 * screen and it re-centres under your finger; drag to steer (x) and accelerate/
 * brake (y). Releasing recentres it. It tracks a single pointer id so the item
 * button on the OTHER side can use a second finger.
 *
 * Output is RAW screen-space: x = +right, y = +forward. The §6.2 player flip is
 * applied later.
 *
 * M115 — every size and curve number comes from a resolved TouchProfile, so BIG
 * KID can swap the whole feel (a much bigger pad, a much gentler curve) without
 * a second code path. Off Big Kid the profile is tuning.touch verbatim.
 */
export interface JoystickReading {
  x: number; // -1..1, + = right
  y: number; // -1..1, + = forward
  active: boolean;
}

/** The grown-up profile: tuning.touch, straight through, no fitting. */
function adultProfile(): TouchProfile {
  const t = tuning.touch;
  return {
    steerDeadzone: t.steerDeadzone,
    steerExpo: t.steerExpo,
    steerSensitivity: t.steerSensitivity,
    steerSmoothRate: t.steerSmoothRate,
    throttleDeadzone: t.throttleDeadzone,
    throttleSensitivity: t.throttleSensitivity,
    reverseSensitivity: t.reverseSensitivity,
    baseRadiusPx: t.baseRadiusPx,
    maxRadiusPx: t.maxRadiusPx,
    restRaisePx: t.restRaisePx,
    restInsetPx: t.restInsetPx,
  };
}

/** The Big Kid profile, shrunk to whatever screen we're actually on. */
function bigKidProfile(viewportW: number, viewportH: number): TouchProfile {
  const k = tuning.touch.bigkid;
  return fitTouchProfile(
    {
      steerDeadzone: k.steerDeadzone,
      steerExpo: k.steerExpo,
      steerSensitivity: k.steerSensitivity,
      steerSmoothRate: k.steerSmoothRate,
      throttleDeadzone: k.throttleDeadzone,
      throttleSensitivity: k.throttleSensitivity,
      reverseSensitivity: k.reverseSensitivity,
      baseRadiusPx: k.baseRadiusPx,
      maxRadiusPx: k.maxRadiusPx,
      restRaisePx: k.restRaisePx,
      restInsetPx: k.restInsetPx,
    },
    viewportW,
    viewportH,
    k.padWidthFrac,
    k.padHeightFrac,
    k.minFitScale,
  );
}

export class VirtualJoystick {
  private base: HTMLDivElement;
  private knob: HTMLDivElement;
  private enabled = true;
  /** Which lower corner the joystick lives in (M31 lefty layout). */
  private side: 'left' | 'right' = 'left';
  private pointerId: number | null = null;
  private originX = 0;
  private originY = 0;
  private x = 0;
  private y = 0;
  /** User steering-sensitivity multiplier (Settings slider); 1 = neutral. */
  private steerSensMult = 1;
  /** M115 — the live feel profile + the two geometry radii derived from it. */
  private profile: TouchProfile = adultProfile();
  private bigKid = false;
  /** M92: a fresh touch INSIDE the visible ring (plus a forgiving lip) GRABS the
   *  stick where it stands instead of re-centring under the finger — so a quick
   *  tap/swipe into the ring's lower half is instant brake/reverse intent. Before
   *  this, every tap re-centred the origin AT the finger and read as neutral (the
   *  owner's "reverse just acts as a brake" stall). */
  private grabRadius = 0;
  /** Re-centred origins keep this much reachable screen in every direction. */
  private edgeReach = 0;
  /** A profile/viewport change that landed mid-drag, applied on release. */
  private profileDirty = false;
  /** M115 — the low-pass-filtered steer actually reported (Big Kid only). */
  private steerOut = 0;
  private lastReadMs = 0;
  private unsubViewport: ViewportUnsubscribe;

  constructor(private container: HTMLElement) {
    this.base = document.createElement('div');
    this.base.className = 'joystick-base';
    this.knob = document.createElement('div');
    this.knob.className = 'joystick-knob';
    this.base.appendChild(this.knob);
    this.container.appendChild(this.base);
    this.applyProfile();

    // M115: the Big Kid pad is viewport-FITTED, so a rotate/resize has to
    // re-resolve it (idempotent re-measure — the M95 subscription contract).
    this.unsubViewport = subscribeViewport(() => this.applyProfile());
    window.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
  }

  /** Re-resolve the feel profile for the live viewport, publish the ring size to
   *  CSS, and re-rest the pad. Deferred while a finger is down (changing the
   *  throw radius mid-drag would jump the car). */
  private applyProfile(): void {
    if (this.pointerId !== null) {
      this.profileDirty = true;
      return;
    }
    this.profile = this.bigKid
      ? bigKidProfile(window.innerWidth, window.innerHeight)
      : adultProfile();
    this.grabRadius = this.profile.baseRadiusPx / 2 + 12;
    this.edgeReach = this.profile.maxRadiusPx + 12;
    // The joystick owns --joy-base (ui.css sizes the ring + knob from it): the
    // Big Kid pad's size depends on the viewport, so only this class knows it.
    document.documentElement.style.setProperty(
      '--joy-base',
      `${Math.round(this.profile.baseRadiusPx)}px`,
    );
    this.restPosition();
  }

  private restPosition(): void {
    // The lower thumb corner (left by default, right when lefty), above the
    // safe-area inset.
    const inset = this.profile.restInsetPx;
    const x = this.side === 'right' ? window.innerWidth - inset : inset;
    const y = window.innerHeight - this.profile.restRaisePx;
    this.placeBase(x, y);
    this.knob.style.transform = 'translate(-50%, -50%)';
  }

  private placeBase(x: number, y: number): void {
    this.originX = x;
    this.originY = y;
    this.base.style.left = `${x}px`;
    this.base.style.top = `${y}px`;
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.enabled) return; // menus are up — driving input is off
    if (this.pointerId !== null) return; // already tracking a finger
    // Reserve the OTHER 35% for the item button so a tap there never grabs the
    // joystick. Default layout reserves the right; lefty reserves the left.
    if (this.side === 'right') {
      if (e.clientX < window.innerWidth * 0.35) return;
    } else if (e.clientX > window.innerWidth * 0.65) {
      return;
    }
    this.pointerId = e.pointerId;
    // Inside the ring = grab the stick where it stands (the touch position IS the
    // input — tap the lower half, get reverse). Outside = the familiar floating
    // re-centre, but CLAMPED off the screen edges so a full throw in every
    // direction (especially DOWN, near the bottom edge) is always reachable.
    const grabbed =
      Math.hypot(e.clientX - this.originX, e.clientY - this.originY) <= this.grabRadius;
    if (!grabbed) {
      const o = clampStickOrigin(
        e.clientX,
        e.clientY,
        window.innerWidth,
        window.innerHeight,
        this.edgeReach,
      );
      this.placeBase(o.x, o.y);
    }
    this.base.classList.add('active');
    this.updateKnob(e.clientX, e.clientY);
  };

  private onMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.updateKnob(e.clientX, e.clientY);
  };

  private onUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.pointerId) return;
    this.pointerId = null;
    this.resetAxes();
    this.base.classList.remove('active');
    this.reRest();
  };

  /** Re-rest the pad, flushing any profile/viewport change that arrived while a
   *  finger was down. */
  private reRest(): void {
    if (this.profileDirty) {
      this.profileDirty = false;
      this.applyProfile(); // re-resolves AND re-rests
    } else {
      this.restPosition();
    }
  }

  /** Zero the axes AND the smoothing state, so a filtered steer can never be
   *  left hanging across a release / a menu. */
  private resetAxes(): void {
    this.x = 0;
    this.y = 0;
    this.steerOut = 0;
    this.lastReadMs = 0;
  }

  private updateKnob(px: number, py: number): void {
    const p = this.profile;
    // M92 rim-follow leash: a finger travelling beyond the rim drags the whole
    // stick along, so a fast long swipe (e.g. full-forward → hard down) always
    // ends at the FULL deflection in the swipe's direction instead of needing a
    // 2×-radius thumb journey from the old origin.
    const o = followStickOrigin(this.originX, this.originY, px, py, p.maxRadiusPx);
    if (o.x !== this.originX || o.y !== this.originY) this.placeBase(o.x, o.y);
    let dx = px - this.originX;
    let dy = py - this.originY;
    const dist = Math.hypot(dx, dy);
    if (dist > p.maxRadiusPx) {
      dx = (dx / dist) * p.maxRadiusPx;
      dy = (dy / dist) * p.maxRadiusPx;
    }
    this.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
    // The knob tracks the finger 1:1 (above); the REPORTED axes are shaped for a
    // mobile-friendly feel — a deadzone + sensitivity + expo on steer, deadzone on
    // gas. Keyboard/gamepad never reach this code, so desktop is untouched (§ shared).
    this.x = shapeAxis(
      dx / p.maxRadiusPx,
      p.steerDeadzone,
      p.steerExpo,
      p.steerSensitivity * this.steerSensMult,
    );
    // Gas: forward keeps its own gain (1 for grown-ups = 1:1 fine control; Big
    // Kid raises it so the bigger pad never makes "hold forward" a reach), and
    // the DOWN half gets reverseSensitivity so any pull into the lower half
    // reads as clear brake/reverse intent.
    const rawY = -dy / p.maxRadiusPx;
    this.y = shapeAxis(
      rawY,
      p.throttleDeadzone,
      0,
      rawY < 0 ? p.reverseSensitivity : p.throttleSensitivity,
    );
  }

  /** Sampled once per sim step. The steer axis passes through the profile's
   *  low-pass (a no-op unless Big Kid is on) using WALL-CLOCK dt, so the amount
   *  of smoothing per real second is the same however many fixed steps the loop
   *  runs in a frame. */
  read(): JoystickReading {
    const now = performance.now();
    const dtS = this.lastReadMs === 0 ? 0 : Math.min(0.05, (now - this.lastReadMs) / 1000);
    this.lastReadMs = now;
    this.steerOut = smoothTowards(this.steerOut, this.x, this.profile.steerSmoothRate, dtS);
    return { x: this.steerOut, y: this.y, active: this.pointerId !== null };
  }

  /** M112 — NON-DESTRUCTIVE sample for the demo-race director. Deliberately the
   *  RAW steer, not the filtered one: the director only threshold-tests "is the
   *  player really steering yet", and the freeze stops read() from advancing the
   *  filter anyway. */
  peek(): JoystickReading {
    return { x: this.x, y: this.y, active: this.pointerId !== null };
  }

  /** Move the joystick to the given lower corner (M31 lefty layout). Re-rests
   *  immediately when idle so the change is visible without a touch. */
  setSide(side: 'left' | 'right'): void {
    if (side === this.side) return;
    this.side = side;
    if (this.pointerId === null) this.restPosition();
  }

  /** User steering-sensitivity multiplier from the Settings slider (≈0.5..1.5).
   *  Higher = full lock with less thumb travel (less reach/strain). Multiplies
   *  whichever profile is live, so the slider still works inside Big Kid. */
  setSteerSensitivity(mult: number): void {
    this.steerSensMult = mult;
  }

  /** M115 — swap the whole touch feel for the BIG KID kids difficulty: a much
   *  bigger pad and a much gentler steering curve, sized for a small child's
   *  index finger on an iPad. */
  setBigKid(on: boolean): void {
    if (on === this.bigKid) return;
    this.bigKid = on;
    this.base.classList.toggle('bigkid', on);
    this.applyProfile();
  }

  /** Hide + ignore touches while menus are up (the HUD layer is off). */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.pointerId = null;
      this.resetAxes();
      this.base.classList.remove('active');
      this.reRest();
    }
    this.base.style.display = enabled ? '' : 'none';
  }

  dispose(): void {
    this.unsubViewport();
    window.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    this.base.remove();
  }
}
